import { useEffect, useState } from 'react';

import useApiRequest from 'Hooks/useApiRequest';

export const QUEUE_PER_PAGE = 10;

/**
 * One page of a unified Home list, plus the filter and page it was asked for.
 *
 * THE SERVER OWNS THE ORDER, THE PAGE AND THE COUNTS. Nothing here sorts,
 * slices or re-totals what came back: the list spans several record types, and
 * a client that re-orders one page of a cross-type set orders the wrong rows
 * perfectly correctly. `rows` is rendered in the order it arrived.
 *
 * Changing the type filter resets to page 1. Filtering to a type with fewer
 * rows than the current offset would otherwise land the user on a page the
 * filtered set does not have.
 *
 * Changing the status resets the TYPE as well as the page. The type options are
 * whatever the selected bucket holds, and a type carried across from Pending
 * into "Rejected by me" is a filter for something the user may never have
 * rejected — an empty table reached by pressing nothing.
 *
 * `initialStatus` is what turns this into a register rather than a queue: pass
 * one and `?status=` is sent and can be changed; leave it out and the list is
 * whatever the endpoint serves by default. My Requests and My Notifications
 * have no such axis.
 *
 * `perPage: null` SENDS NO SIZE AT ALL, leaving the endpoint's own default to
 * apply and reading it back off the response. A surface whose page size is the
 * server's decision must not restate the number here, where the two copies can
 * drift apart silently.
 *
 * `refresh()` is what a completed modal calls. It re-reads the same page rather
 * than patching a row in place — an actioned row moves bucket, and an optimistic
 * edit would have to reproduce that rule on the client.
 */
export default function useHomeQueue(url, { perPage = QUEUE_PER_PAGE, initialStatus = null } = {}) {
    const [type, setType] = useState(null);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState(initialStatus);

    const request = useApiRequest({ url, displayErrorToast: false });

    const fetch = () => request.get({ page, ...(perPage ? { per_page: perPage } : {}), ...(type ? { type } : {}), ...(status ? { status } : {}) });

    useEffect(() => {
        fetch();
    }, [url, type, page, status]);

    const filterByType = (next) => {
        setPage(1);
        setType(next);
    };

    const filterByStatus = (next) => {
        setPage(1);
        setType(null);
        setStatus(next);
    };

    const queue = request.data?.data;

    return {
        queue,
        rows: queue?.rows ?? [],
        countsByType: queue?.counts_by_type ?? {},
        loading: request.loading,
        error: request.error,
        type,
        page,
        /* What the SERVER actually paged by, falling back to what was asked for
           only until the first response lands. A caller passing no `perPage`
           leaves the size to the endpoint's own default and reads it back here,
           so the figure is not written down twice — and falls through to the
           shared default meanwhile, rather than handing a consumer a null to do
           arithmetic against. */
        perPage: queue?.per_page ?? perPage ?? QUEUE_PER_PAGE,
        status,
        filterByType,
        filterByStatus,
        setPage,
        refresh: fetch,
    };
}
